import { Button, useTheme } from "@mui/material";
import { ArrowLeft20Px, ConfirmationModal } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";

import { setHoldTypeFilters } from "./inventory.slice";
import { useViewType } from "./useViewType";

type InventoryBackButtonProps = {
  isAdjustingBins: boolean;
  isGetBinsLoading: boolean;
  handleFetchAllInventory: () => void;
};

export const InventoryBackButton = ({
  isAdjustingBins,
  isGetBinsLoading,
  handleFetchAllInventory
}: InventoryBackButtonProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const trackedPageName = useMixpanelPageName();
  const { isHoldsPage } = useViewType();
  const { variantId: variantIdParam, binNumber: binNumberParam } = useParams<{
    variantId?: string;
    binNumber?: string;
  }>();
  const dispatch = useAppDispatch();
  const isAutostorePutawayDialogOpen = useAppSelector(
    (state) => state.inventoryNew.isAutostorePutawayDialogOpen
  );
  const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);

  if (!(isAdjustingBins || variantIdParam || binNumberParam)) {
    return null;
  }

  const isDisabled = isGetBinsLoading || isAutostorePutawayDialogOpen;

  return (
    <>
      <Button
        variant="text"
        onClick={() => {
          if (isAdjustingBins || isGetBinsLoading) {
            setIsRefreshModalOpen(true);
          } else {
            handleFetchAllInventory();
            dispatch(setHoldTypeFilters([]));
          }
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: isHoldsPage
              ? "Return to inventory holds"
              : "Return to all inventory"
          });
        }}
        startIcon={
          <ArrowLeft20Px
            fill={theme.palette.autostoreDesignSystem.icon.onStandard.primary}
            opacity={isDisabled ? 0.25 : 1}
            width={20}
            height={20}
          />
        }
        disabled={isDisabled}
        sx={{
          alignSelf: "flex-start",
          color: theme.palette.autostoreDesignSystem.text.onStandard.primary,
          display: "flex",
          height: 31,
          minHeight: 31,
          padding: "0 20px 0 8px",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          borderRadius: "8px",
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: 1.35,
          "& .MuiButton-startIcon": {
            margin: 0
          }
        }}
      >
        {t("back")}
      </Button>
      <ConfirmationModal
        labelledById="return-to-all-inventory-modal-title"
        isOpen={isRefreshModalOpen}
        confirmCb={() => {
          handleFetchAllInventory();
          dispatch(setHoldTypeFilters([]));
          setIsRefreshModalOpen(false);
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Confirm Return to all inventory"
          });
        }}
        closeCb={() => {
          setIsRefreshModalOpen(false);
          mixpanelTrack({
            trackedPageName,
            type: "Button Click",
            label: "Cancel Return to all inventory"
          });
        }}
        modalTitle={
          isHoldsPage
            ? t("return to inventory holds")
            : t("return to all inventory")
        }
        modalText={
          isHoldsPage
            ? t(
                "are you sure you want to close all current bins and return to the inventory holds page?"
              )
            : t(
                "are you sure you want to close all current bins and return to the inventory main page?"
              )
        }
      />
    </>
  );
};
